<template>
  <div class="opportunities d-flex flex-column">
    <div class="sourcery__container">
      <div class="opportunities__header">
        <div class="opportunities__header-content">
          <h1 class="fs-42 font-weight-bold lh-normal">
            {{ pageContent.header.title }}
          </h1>
          <p>
            {{ pageContent.header.subtext }}
          </p>
          <v-btn
            outlined
            :href="pageContent.header.demoLink"
            target="_blank">
            Schedule a Meeting with Us
          </v-btn>
        </div>
        <div class="opportunities__header-image">
          <ListingImage
            :pictures="setPictures({
              logo: getActiveWorkspaceLogoKey,
            })" />
        </div>
      </div>
      <!-- description + keywords -->
      <div class="opportunities__description mt-8">
        <h2 class="fs-32">
          {{ pageContent.workspaceInfo.title }}
        </h2>

        <p class="mt-4">
          {{ pageContent.workspaceInfo.subtext }}
        </p>
        <v-textarea
          v-model="workspaceInfo.description"
          :rows="3"
          class="mt-2"
          placeholder="Description"
          auto-grow
          dense
          hide-details
          outlined />

        <AppKeywordInput
          v-model="workspaceInfo.keywords"
          class="mt-2" />

        <v-btn
          :disabled="!hasChange"
          class="mt-6"
          outlined
          @click="updateWorkspaceInfo">
          Update Workspace Info
        </v-btn>
      </div>
    </div>

    <!-- Recent -->
    <section class="darkBlue">
      <div class="sourcery__container">
        <h2 class="fs-32">
          {{ pageContent.recent.title }}
        </h2>
        <p class="mt-6">
          {{ pageContent.recent.subtext }}
        </p>

        <h3 class="mt-6 fs-24 font-weight-regular lh-normal">
          Page and Collection Views
        </h3>
        <p class="mt-6">
          {{ pageContent.recent.viewsText }}
        </p>
        <DataTable
          class="mt-4"
          :dark="true"
          :headers="viewsHeaders"
          :data="recentCollAndPageViews" />

        <h3 class="mt-6 fs-24 font-weight-regular lh-normal">
          Project Adds
        </h3>
        <p class="mt-6">
          {{ pageContent.recent.projAddsText }}
        </p>
        <DataTable
          class="mt-4"
          :dark="true"
          :headers="projectsHeaders"
          :data="recentProjectAdds" />
      </div>
    </section>

    <!-- Historical -->
    <section class="sourcery__container">
      <h2 class="fs-32">
        {{ pageContent.historical.title }}
      </h2>
      <p class="mt-6">
        {{ pageContent.historical.subtext }}
      </p>

      <h3 class="mt-6 fs-24 font-weight-regular lh-normal">
        Historical Page and Collection View
      </h3>
      <DataTable
        class="mt-4"
        :headers="viewsHeaders"
        :data="collAndPageViews" />

      <h3 class="mt-6 fs-24 font-weight-regular lh-normal">
        Historical Collection Adds
      </h3>
      <p class="mt-6">
        {{ pageContent.historical.collAddsText }}
      </p>
      <DataTable
        class="mt-4"
        :headers="collAddsHeaders"
        :data="collAdds" />

      <h3 class="mt-6 fs-24 font-weight-regular lh-normal">
        Historical Project Adds
      </h3>
      <p class="mt-6">
        {{ pageContent.historical.projAddsText }}
      </p>
      <DataTable
        class="mt-4"
        :headers="projectsHeaders"
        :data="projectAdds" />
    </section>

    <!-- Conversion -->
    <section class="darkBlue">
      <div class="sourcery__container">
        <h2 class="fs-32">
          {{ pageContent.conversion.title }}
        </h2>
        <p class="mt-6 mb-0">
          {{ pageContent.conversion.subtext }}
        </p>
        <div class="d-flex flex-column align-center justify-center gap-1">
          <DataDonut
            :labels="['Project Spec Percentage']"
            :series="conversionSeries"
            :center-text="projectSpecPercentage + '%'" />
          <p class="mt-n10 fs-16 font-weight-regular text-center">
            Conversion Rate <br>
            From initial product views and collection adds to final project specifications
          </p>
        </div>
        <p>
          {{
            replacePlaceholders(pageContent.conversion.convText, {
              percentage: projectSpecPercentage + '%',
            })
          }}
        </p>

        <h2 class="mt-6 fs-32">
          {{ pageContent.conversion.strategies.title }}
        </h2>
        <p class="mt-6">
          {{
            replacePlaceholders(pageContent.conversion.strategies.subtext, {
              percentage: projectSpecPercentage + '%',
            })
          }}
        </p>

        <div class="opportunities__grid mt-6">
          <v-card
            v-for="(card, index) in pageContent.conversion.strategies.cards"
            :key="index">
            <v-card-title>
              {{ card.title }}
            </v-card-title>
            <v-card-text>
              {{ card.text }}
            </v-card-text>
          </v-card>
          <v-card class="opportunities__grid--cols-expanded">
            <v-card-text>
              <b>{{ pageContent.conversion.strategies.timing.headline }}:</b> {{ pageContent.conversion.strategies.timing.text }}
            </v-card-text>
          </v-card>
        </div>
      </div>
    </section>

    <!-- Top Products -->
    <section class="sourcery__container mb-0">
      <h2 class="fs-32">
        {{
          replacePlaceholders(pageContent.engagement.title, {
            workspaceName: getActiveWorkspaceName,
          })
        }}
      </h2>
      <p class="mt-6">
        {{ pageContent.engagement.subtext }}
      </p>
      <HorizontalBarChart
        :data="topEngagingProducts"
        series-name="Engagements"
        label-key="model"
        value-key="engagements" />
      <p class="mt-4">
        {{
          replacePlaceholders(pageContent.engagement.chartText, {
            topEngagingProductsText: topEngagingProduct,
          })
        }}
      </p>

      <!-- maximize engagement -->
      <v-list class="mt-8 pa-0 lightGrey">
        <v-list-group
          :value="false"
          color="black"
          sub-group>
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title class="fs-20 font-weight-regular lh-normal">
                {{ pageContent.engagement.maximize.title }}
              </v-list-item-title>
            </v-list-item-content>
          </template>

          <v-card class="elevation-0 border-thin">
            <v-card-text class="pa-10">
              <div class="opportunities__grid">
                <!-- strategic priorities -->
                <div>
                  <h4 class="fs-24 black--text font-weight-regular lh-normal">
                    {{ pageContent.engagement.maximize.prioritiesHeader }}
                  </h4>
                  <p class="mt-4">
                    {{ pageContent.engagement.maximize.prioritiesText }}
                  </p>

                  <h5 class="fs-16 black--text lh-normal">
                    Key actions to take:
                  </h5>
                  <ul class="mt-4 black--text">
                    <li
                      v-for="(item, index) in pageContent.engagement.maximize.keyActions"
                      :key="index">
                      {{ item }}
                    </li>
                  </ul>
                </div>

                <!-- understanding behavior -->
                <div>
                  <h4 class="fs-24 black--text font-weight-regular lh-normal">
                    {{ pageContent.engagement.maximize.behaviorHeader }}
                  </h4>
                  <p class="mt-4">
                    {{ pageContent.engagement.maximize.behaviorText }}
                  </p>
                </div>
              </div>

              <div class="opportunities__grid mt-8">
                <!-- key actions -->
                <div
                  v-for="(item, index) in pageContent.engagement.maximize.actions"
                  :key="index">
                  <span class="black--text d-block border-bottom">{{ String(index + 1).padStart(2, '0') }}</span>
                  <span class="mt-2 d-block fs-20 font-weight-regular black--text">{{ item.title }}</span>
                  <p class="mt-4">
                    {{ item.text }}
                  </p>
                </div>

                <p class="opportunities__grid--cols-expanded">
                  {{ pageContent.engagement.maximize.footer }}
                </p>
              </div>
            </v-card-text>
          </v-card>
        </v-list-group>
      </v-list>

      <!-- contact -->
      <v-list class="mt-8 pa-0 lightGrey">
        <v-list-group
          :value="false"
          color="black"
          sub-group>
          <template v-slot:activator>
            <v-list-item-content>
              <v-list-item-title class="fs-20 font-weight-regular lh-normal">
                Take Action on Your Insights
              </v-list-item-title>
            </v-list-item-content>
          </template>

          <v-card class="elevation-0 border-thin">
            <v-card-text class="pa-10 opportunities__grid opportunities__grid-3">
              <div
                v-for="(item, index) in pageContent.engagement.actions"
                :key="index"
                class="lightGrey pa-6 rounded-lg">
                <span class="d-block fs-20 font-weight-regular black--text">
                  {{ item.title }}
                </span>
                <span class="mt-4 d-block fs-16 font-weight-regular black--text">
                  {{ item.text }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-list-group>
      </v-list>

      <div class="mt-8 opportunities__grid-4">
        <div class="opportunities__grid-4--expanded">
          <h3 class="mt-6 fs-24 font-weight-regular lh-normal">
            {{ pageContent.engagement.contact.title }}
          </h3>
          <p
            v-safe-html="pageContent.engagement.contact.text"
            class="mt-4" />
          <div class="mt-6 d-flex flex-row flex-wrap gap-2">
            <v-btn
              :href="pageContent.engagement.contact.demoLink"
              target="_blank"
              outlined>
              Schedule Demo
            </v-btn>
            <v-btn
              :href="pageContent.engagement.contact.supportLink"
              target="_blank"
              outlined>
              Contact Support
            </v-btn>
          </div>
        </div>

        <!-- Need more detail? -->
        <div class="lightGrey pa-6 rounded-lg">
          <h3 class="fs-20 font-weight-regular lh-normal">
            {{ pageContent.engagement.detail.title }}
          </h3>
          <p
            v-safe-html="pageContent.engagement.detail.text"
            class="mt-4" />
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  mapState,
  mapGetters,
  mapMutations,
  mapActions,
} from 'vuex';

import AppKeywordInput from '@/components/App/AppKeywordInput';
import DataDonut from '@/components/Opportunities/DataDonut';
import DataTable from '@/components/Opportunities/DataTable';
import HorizontalBarChart from '@/components/Opportunities/HorizontalBarChart';
import ListingImage from '@/components/Listing/ListingImageItem/ListingImage';

import SetPictures from '@/mixins/SetPictures';

import OpportunitiesAPI from '@/services/graphql/opportunities';

import OppsPageContent from '@/dataFront/opportunities';

export default {
  name: 'Opportunities',
  components: {
    AppKeywordInput,
    DataDonut,
    DataTable,
    HorizontalBarChart,
    ListingImage,
  },
  mixins: [SetPictures],
  data() {
    return {
      // Collection and Page Views
      collAndPageViews: [],
      recentCollAndPageViews: [],
      viewsHeaders: [
        {
          text: 'Workspace',
          value: 'workspace',
        },
        {
          text: 'Action',
          value: 'action',
        },
        {
          text: 'Collection Name',
          value: 'collectionName',
        },
      ],

      // Project Adds
      projectAdds: [],
      recentProjectAdds: [],
      projectsHeaders: [
        {
          text: 'Workspace',
          value: 'workspace',
        },
        {
          text: 'Action',
          value: 'action',
        },
        {
          text: 'Project Location',
          value: 'projectLocation',
        },
        {
          text: 'Product Added',
          value: 'productAdded',
        },
        {
          text: 'Date',
          value: 'dateAdded',
        },
      ],

      // Collection Adds
      collAdds: [],
      collAddsHeaders: [

        {
          text: 'Workspace',
          value: 'workspaceAddedTo',
        },
        {
          text: 'Action',
          value: 'action',
        },
        {
          text: 'Product Added',
          value: 'productAdded',
        },
      ],
      // Other
      conversionData: {
        collAdds: 0,
        projectSpecs: 0,
        conversionRate: 0,
      },
      topEngagingProducts: [],

      // all the text
      pageContent: OppsPageContent,

      // for workspace info
      workspaceInfo: {
        description: '',
        keywords: [],
      },
    };
  },
  computed: {
    ...mapState('Workspace', ['activeWorkspaceId']),
    ...mapGetters('Workspace', ['getActiveWorkspace', 'getActiveWorkspaceLogoKey', 'getActiveWorkspaceName']),
    hasChange() {
      return this.workspaceInfo.description !== this.getActiveWorkspace.description
        || this.workspaceInfo.keywords?.join(',') !== this.getActiveWorkspace.keywords?.join(',');
    },
    projectSpecPercentage() {
      const total = Math.max(this.conversionData.collAdds, 1);
      const specRatio = this.conversionData.projectSpecs / total;
      return Math.round(specRatio * 100);
    },
    conversionSeries() {
      return [this.projectSpecPercentage];
    },
    topEngagingProduct() {
      if (this.topEngagingProducts.length === 0) return '';
      return this.topEngagingProducts[0].model;
    },
  },
  watch: {
    '$route.params.wId': {
      async handler() {
        this.initWorkspaceInfo();
        await this.fetchDashboardData();
      },
    },
  },
  mounted() {
    this.initWorkspaceInfo();
    this.fetchDashboardData();
  },
  methods: {
    ...mapMutations(['spinner', 'openSnackBar']),
    ...mapActions('Workspace', ['editWorkspace']),
    async fetchDashboardData() {
      this.spinner(true);
      let parsed;
      try {
        const res = await OpportunitiesAPI.getDashboardData({
          workspaceId: this.activeWorkspaceId,
        });
        parsed = JSON.parse(res.data.dashboardData);
      } catch (error) {
        this.openSnackBar({
          title: 'Error fetching dashboard data. Please try again later',
          color: 'red',
          timeout: 3000,
        });
      } finally {
        this.spinner(false);
        this.collAndPageViews = this.formatViewsData(parsed?.collAndPageViews || []);
        this.recentCollAndPageViews = this.formatViewsData(parsed?.recentCollAndPageviews || []);

        this.projectAdds = this.formatProjectData(parsed?.projectAdds || []);
        this.recentProjectAdds = this.formatProjectData(parsed?.recentProjectAdds || []);

        this.collAdds = this.formatCollAddsData(parsed?.collAdds || []);

        this.conversionData = parsed?.conversionRate || this.conversionData;
        this.topEngagingProducts = parsed?.topEngagingProducts || [];
      }
    },
    formatViewsData(data) {
      return data.map(item => ({
        workspace: item.workspace,
        action: item.type === 'COLLECTION' ? 'Viewed your Collection' : 'Viewed your Page',
        collectionName: item.type === 'COLLECTION' ? item.name : 'N/A',
      }));
    },
    formatProjectData(data) {
      return data.map(item => ({
        workspace: item.workspace,
        action: 'Added to Project',
        projectLocation: item.projectLocation,
        productAdded: item.productAdded,
        dateAdded: this.formatDate(item.dateAdded),
      }));
    },
    formatCollAddsData(data) {
      return data.map(item => ({
        workspaceAddedTo: item.workspaceAddedTo,
        action: 'Added to Collection',
        productAdded: item.productAdded,
      }));
    },
    formatDate(timestamp) {
      if (!timestamp) return '-';
      return this.$moment(timestamp).format('DD/MM/YYYY');
    },
    initWorkspaceInfo() {
      this.workspaceInfo = {
        description: this.getActiveWorkspace.description || '',
        keywords: this.getActiveWorkspace.keywords || [],
      };
    },
    replacePlaceholders(template, data) {
      let result = template;
      for (const key in data) {
        const regex = new RegExp(`{{${key}}}`, 'g');
        const value = String(data[key]);
        result = result.replace(regex, value);
      }
      return result;
    },
    async updateWorkspaceInfo() {
      this.spinner(true);
      try {
        await this.editWorkspace({
          workspaceId: this.activeWorkspaceId,
          description: this.workspaceInfo.description,
          keywords: this.workspaceInfo.keywords,
          name: this.getActiveWorkspace.name,
          logo: this.getActiveWorkspace.logo,
        });
      } catch (error) {
        this.openSnackBar({
          title: 'Error updating workspace info. Please try again later',
          color: 'red',
          timeout: 3000,
        });
      } finally {
        this.spinner(false);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.opportunities {
  p, h1, h2, h3, h4, h5, h6, span:not(.v-chip) {
    font-family: Poppins !important;
  }

  &__header {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 40px;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      flex-wrap: nowrap;
    }
  }

  &__header-content {
    display: flex;
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  &__header-image {
    aspect-ratio: 305 / 201;
    flex-basis: 300px;
    flex-grow: 0;
    flex-shrink: 0;
    order: -1;
    height: fit-content;

    border-radius: 15px;
    overflow: hidden;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      order: 0;
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      grid-template-columns: repeat(2, 1fr);
    }

    &--cols-expanded {
      grid-column: 1 / -1;
    }
  }

  &__grid-3 {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  &__grid-4 {
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 16px;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      grid-template-columns: repeat(4, 1fr);
    }

    &--expanded {
      grid-column: span 3;
    }
  }

  .border-bottom {
    border-bottom: 1px solid var(--v-black-base);
  }

  .border-thin {
    border: 1px solid rgba(var(--v-blackRGB), 0.25);
  }

  .darkBlue {
    background-color: var(--v-darkBlue-base);
    color: var(--v-white-base);

    p {
      color: var(--v-white-base);
    }
  }

  .sourcery__container {
    margin-block: 74px;
  }
}
</style>